import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Row,
  Col,
  Modal,
  Table,
  Container,
  Accordion,
} from "react-bootstrap";
import "./net-rates.scss";
import { capitalizeString } from "../../utils/common";
import { DateTime } from "luxon";
import { t } from "i18next";
import _ from "lodash";
import { toast } from "react-toastify";

type NetRate = {
  rackRate: number | null;
  twilightRate: number | null;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  error?: {
    rackRate?: string;
    twilightRate?: string;
    dateConflict?:string;
  };
};

type NetRateData = {
  id: string;
  name: string;
  provinceName:string;
  countryName:string;
  netRates:{
    netRateEnabled: boolean,
    rates:NetRate[]
  }
};

const TeeTimeManager = (props: any) => {
  let { netRates, handleNetRateUpdate } = props;

  const [showYearModal, setShowYearModal] = useState(false);
  const [yearForMonthlyRates, setYearForMonthlyRates] = useState(DateTime.now().year);
  const [selectedClubForYearAdd, setSelectedClubForYearAdd] = useState<string | null>(null);

  const [selectedCountry, setSelectedCountry] = useState<string>("All");
  const [selectedState, setSelectedState] = useState<string>("All");

  const [clubSettings, setClubSettings] = useState<NetRateData[]>([]);
  const [selectedClubForModal, setSelectedClubForModal] = useState<{
    club: NetRateData;
    rateIndex: number;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [teeTimes, setTeeTimes] = useState<{ time: string; rate: number }[]>([]);

  const countries = React.useMemo(() => {
  return ["All", ..._.uniq(clubSettings.map(c => c.countryName).filter(Boolean))];
}, [clubSettings]);

  const states = React.useMemo(() => {
    if (selectedCountry === "All") return ["All"];

    return [
      "All",
      ..._.uniq(
        clubSettings
          .filter(c => c.countryName === selectedCountry)
          .map(c => c.provinceName)
          .filter(Boolean)
      ),
    ];
  }, [clubSettings, selectedCountry]);


  // ✅ Initialize with safe structure
  useEffect(() => {
    let clonedRates = _.cloneDeep(netRates);
    let newRates = clonedRates?.map((club: any) => {
      if (!club.netRates || !Array.isArray(club.netRates.rates)) {
        club.netRates = {
          netRateEnabled: club.netRates?.netRateEnabled ?? false,
          rates: Array.isArray(club.netRates) ? club.netRates : [],
        };
      }

      club.netRates.rates.sort((a: NetRate, b: NetRate) =>
        DateTime.fromISO(a.startDate) < DateTime.fromISO(b.startDate) ? -1 : 1
      );
      return club;
    });
    setClubSettings(newRates);
  }, [netRates]);

  // Helpers
  const parseTimeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const formatTime = (totalMinutes: number) => {
    let hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const paddedMinutes = minutes.toString().padStart(2, "0");
    return `${hours}:${paddedMinutes} ${ampm}`;
  };

  const generateTeeTimes = (rate: NetRate) => {
    const { startTime, endTime, rackRate, twilightRate } = rate ?? {};
    if (!startTime || !endTime) return;
    const start = parseTimeToMinutes(startTime);
    const end = parseTimeToMinutes(endTime);
    const twilightStart = parseTimeToMinutes("15:00");

    const teeTimesArray = [];
    for (let i = start; i + 30 <= end; i += 30) {
      const startSlot = formatTime(i);
      const endSlot = formatTime(i + 30);
      const rate = i >= twilightStart ? Number(twilightRate) : Number(rackRate);
      teeTimesArray.push({ time: `${startSlot} - ${endSlot}`, rate });
    }
    return teeTimesArray;
  };

  // Input change
  const handleInputChange = (
  clubId: string,
  rateIndex: number,
  field: keyof NetRate,
  value: string
) => {
  setClubSettings((prev) =>
    prev.map((club) => {
      if (club.id !== clubId) return club;

      const updatedRates = club.netRates.rates.map((rate, idx) => {
        if (idx !== rateIndex) return rate;

        let updatedRate = { ...rate, [field]: value };

        // If startDate changes and is after endDate, update endDate to match startDate
        if (field === "startDate" && DateTime.fromISO(value) > DateTime.fromISO(rate.endDate)) {
          updatedRate.endDate = value;
        }

        return updatedRate;
      });

      return { ...club, netRates: { ...club.netRates, rates: updatedRates } };
    })
  );
};

const hasMissingPrices = (rate:any) => {
  return (
    rate.rackRate === null ||
    rate.rackRate === "" ||
    rate.twilightRate === null ||
    rate.twilightRate === ""
  );
};


const addMonthlyRatesByYear = (clubId: string, year: number) => {
  setClubSettings((prev) =>
    prev.map((club) => {
      if (club.id !== clubId) return club;

      const existing = club.netRates.rates;

      // Only take months for the selected year
      const existingMonthsForYear = new Set(
        existing
          .filter((rate) => DateTime.fromISO(rate.startDate).year === year)
          .map((rate) => DateTime.fromISO(rate.startDate).month)
      );

      const newRates: NetRate[] = [];

      for (let month = 1; month <= 12; month++) {
        if (!existingMonthsForYear.has(month)) {
          const startOfMonth = DateTime.fromObject({ year, month }).toFormat(
            "yyyy-MM-dd"
          );
          const endOfMonth = DateTime.fromObject({ year, month })
            .endOf("month")
            .toFormat("yyyy-MM-dd");

          newRates.push({
            rackRate: null,
            twilightRate: null,
            startDate: startOfMonth,
            endDate: endOfMonth,
            startTime: "07:00",
            endTime: "18:00",
          });
        }
      }

      if (newRates.length === 0) {
        toast.info(`All 12 months for ${year} are already added!`);
        return club;
      }

      return {
        ...club,
        netRates: {
          ...club.netRates,
          rates: [...existing, ...newRates]?.sort((a: NetRate, b: NetRate) =>
            DateTime.fromISO(a.startDate) < DateTime.fromISO(b.startDate) ? -1 : 1
          ),
        },
      };
    })
  );
};
  // Add new rate
  const handleAddRate = (clubId: string) => {
    setClubSettings((prev) =>
      prev.map((club) =>
        club.id === clubId
          ? {
              ...club,
              netRates: {
                ...club.netRates,
                rates: [
                  ...club.netRates.rates,
                  {
                    rackRate: null,
                    twilightRate: null,
                    startDate: DateTime.now().toFormat("yyyy-MM-dd"),
                    endDate: DateTime.now().plus({ days: 1 }).toFormat("yyyy-MM-dd"),
                    startTime: "07:00",
                    endTime: "18:00",
                  },
                ],
              },
            }
          : club
      )
    );
  };

  // Delete rate
  const handleDeleteRate = (clubId: string, rateIndex: number) => {
    setClubSettings((prev) =>
      prev.map((club) =>
        club.id === clubId
          ? {
              ...club,
              netRates: {
                ...club.netRates,
                rates: club.netRates.rates.filter((_, idx) => idx !== rateIndex),
              },
            }
          : club
      )
    );
  };

  // Toggle enable
  const handleToggleClubRates = (clubId: string, enabled: boolean) => {
    setClubSettings((prev) =>
      prev.map((club) =>
        club.id === clubId
          ? { ...club, netRates: { ...club.netRates, netRateEnabled: enabled } }
          : club
      )
    );
  };

  // View Tee Times
  const handleViewTeeTimes = (club: NetRateData, rateIndex: number) => {
    const rate = club.netRates.rates[rateIndex];
    const times = generateTeeTimes(rate);
    if (times) setTeeTimes(times);
    setSelectedClubForModal({ club, rateIndex });
    setShowModal(true);
  };

  // Submit handler
  function submitHandler() {
    let isAnyError = false;

    const newSettings = clubSettings.map((club) => {
      if (!club.netRates.netRateEnabled) return club;

      const updatedRates = club.netRates.rates.map((rate, idx, arr) => {
        let error: any = {};

        // Required field validation
        if (!rate.rackRate || rate.rackRate <= 0) {
          isAnyError = true;
          error.rackRate = t("NetRatePage.errorRackRate");
        }
        if (!rate.twilightRate || rate.twilightRate <= 0) {
          isAnyError = true;
          error.twilightRate = t("NetRatePage.errorTwilightRate");
        }

        // Date conflict validation
        const currentStart = DateTime.fromISO(rate.startDate);
        const currentEnd = DateTime.fromISO(rate.endDate);

        arr.forEach((otherRate, otherIdx) => {
          if (idx === otherIdx) return; // skip self
          const otherStart = DateTime.fromISO(otherRate.startDate);
          const otherEnd = DateTime.fromISO(otherRate.endDate);

          const overlap =
            currentStart <= otherEnd && currentEnd >= otherStart;

          if (overlap) {
            isAnyError = true;
            error.dateConflict = "Start/End date is conflicting with another rate";
          }
        });

        return {
          ...rate,
          rackRate: parseFloat(Number(rate.rackRate).toFixed(2)),
          twilightRate: parseFloat(Number(rate.twilightRate).toFixed(2)),
          error,
        };
      });

      return { ...club, netRates: { ...club.netRates, rates: updatedRates } };
    });

    setClubSettings(newSettings);

    if (isAnyError) {
      toast.error("Error in net rate form!");
      return;
    }

    handleNetRateUpdate({ data: newSettings });
  }

  const filteredClubs = React.useMemo(() => {
  return clubSettings.filter((club) => {
    if (selectedCountry !== "All" && club.countryName !== selectedCountry) {
      return false;
    }
    if (selectedState !== "All" && club.provinceName !== selectedState) {
      return false;
    }
    return true;
  });
}, [clubSettings, selectedCountry, selectedState]);

  return (
    <Container className="rackRate mt-4" style={{ maxWidth: "900px" }}>
      <h4 className="mb-4">{t("NetRatePage.title")}</h4>

      <Row className="mb-3 pb-3 align-items-end">
        <Col md={4}>
          <Form.Label>Country</Form.Label>
          <Form.Select
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setSelectedState("All"); // reset state when country changes
            }}
          >
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={4}>
          <Form.Label>Province / State</Form.Label>
          <Form.Select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            disabled={selectedCountry === "All"}
          >
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={4} className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSelectedCountry("All");
              setSelectedState("All");
            }}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>


          <Accordion defaultActiveKey="0" className="itineryAccordion">
            {filteredClubs.map((club, index) => (
              <Accordion.Item eventKey={index.toString()} key={club.id}>
                <Accordion.Header>{capitalizeString(club.name??"")} {club?.netRates?.rates?.find((obj:any)=>Object.keys(obj?.error ?? {})?.length>0) && <i className="p-2 fa-solid fa-exclamation" style={{"color":"red"}}></i>}</Accordion.Header>
                <Accordion.Body>
                  {/* Club-level enable switch */}
                  <Form.Check
                    type="switch"
                    id={`club-switch-${club.id}`}
                    label={t("NetRatePage.form.enableNetRate")}
                    checked={club.netRates.netRateEnabled}
                    onChange={(e) => handleToggleClubRates(club.id, e.target.checked)}
                    className="mb-3"
                  />

                  {/* Nested Accordion for Rates */}
                  <Accordion defaultActiveKey="0">
                    {club.netRates.rates.map((settings, rateIndex) => {
                      const isDisabled = !club.netRates.netRateEnabled;
                      return (
                        <Accordion.Item
                          eventKey={`${rateIndex}`}
                          key={`${club.id}-${rateIndex}`}
                        >
                          <Accordion.Header>
                            <div className="d-flex justify-content-between w-100 mx-2">
                              <span>
                                {t("NetRatePage.form.rate")} {rateIndex + 1} : {settings?.startDate && DateTime.fromFormat(settings.startDate, "yyyy-MM-dd").toFormat("dd MMM, yyyy")} - {settings?.endDate && DateTime.fromFormat(settings.endDate, "yyyy-MM-dd").toFormat("dd MMM, yyyy")}
                                {(hasMissingPrices(settings) || Object.keys(settings?.error ?? {})?.length>0) && (
                                  <i className="p-2 fa-solid fa-exclamation" style={{"color":"red"}}></i>
                                )}
                              </span>
                               
                            </div>
                            
                          </Accordion.Header>
                          <Accordion.Body>
                            <Row>
                              <Col>
                                <Form.Label>{t("NetRatePage.form.startDate")}</Form.Label>
                                <Form.Control
                                  type="date"
                                   onClick={(e:any) => {
                                    if (!isDisabled && e.target?.showPicker) {
                                      e.target?.showPicker(); // Force open the native time dropdown
                                    }
                                  }}
                                  value={settings.startDate}
                                  disabled={isDisabled}
                                  min={DateTime.now().toFormat("yyyy-MM-dd")}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "startDate", e.target.value)
                                  }
                                  isInvalid={!!settings.error?.dateConflict}

                                />
                                <Form.Control.Feedback type="invalid">
                                  {settings.error?.dateConflict}
                                </Form.Control.Feedback>
                              </Col>
                              <Col>
                                <Form.Label>{t("NetRatePage.form.endDate")}</Form.Label>
                                <Form.Control
                                  type="date"
                                   onClick={(e:any) => {
                                    if (!isDisabled && e.target?.showPicker) {
                                      e.target?.showPicker(); // Force open the native time dropdown
                                    }
                                  }}
                                  value={settings.endDate}
                                  disabled={isDisabled}
                                  min={settings.startDate}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "endDate", e.target.value)
                                  }
                                  isInvalid={!!settings.error?.dateConflict}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {settings.error?.dateConflict}
                                </Form.Control.Feedback>
                              </Col>
                              <Col>
                                <Form.Label>{t("NetRatePage.form.startTime")}</Form.Label>
                                <Form.Control
                                  type="time"
                                   onClick={(e:any) => {
                                    if (!isDisabled && e.target?.showPicker) {
                                      e.target?.showPicker(); // Force open the native time dropdown
                                    }
                                  }}
                                  value={settings.startTime}
                                  disabled={isDisabled}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "startTime", e.target.value)
                                  }
                                />
                              </Col>
                              <Col>
                                <Form.Label>{t("NetRatePage.form.endTime")}</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={settings.endTime}
                                   onClick={(e:any) => {
                                    if (!isDisabled && e.target?.showPicker) {
                                      e.target?.showPicker(); // Force open the native time dropdown
                                    }
                                  }}
                                  disabled={isDisabled}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "endTime", e.target.value)
                                  }
                                />
                              </Col>
                            </Row>

                            <Row className="mt-2">
                              <Col>
                                <Form.Label>{t("NetRatePage.form.rackRate")}</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={settings.rackRate ?? ""}
                                  disabled={isDisabled}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "rackRate", e.target.value)
                                  }
                                  isInvalid={!!settings.error?.rackRate}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {settings.error?.rackRate}
                                </Form.Control.Feedback>
                              </Col>
                              <Col>
                                <Form.Label>{t("NetRatePage.form.twilightRate")}</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={settings.twilightRate ?? ""}
                                  disabled={isDisabled}
                                  onChange={(e) =>
                                    handleInputChange(club.id, rateIndex, "twilightRate", e.target.value)
                                  }
                                  isInvalid={!!settings.error?.twilightRate}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {settings.error?.twilightRate}
                                </Form.Control.Feedback>
                              </Col>
                            </Row>

                            <div className="mt-3 d-flex justify-content-between">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={isDisabled}
                                onClick={() => handleViewTeeTimes(club, rateIndex)}
                              >
                                {t("NetRatePage.form.viewTeeTimes")}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={isDisabled}
                                onClick={() => handleDeleteRate(club.id, rateIndex)}
                              >
                                {t("NetRatePage.form.deleteRate")}
                              </Button>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>

                  {/* Add Rate Button */}
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleAddRate(club.id)}
                      disabled={!club.netRates.netRateEnabled}
                    >
                      + {t("NetRatePage.form.addRate")}
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setSelectedClubForYearAdd(club.id);
                        setShowYearModal(true);
                      }}
                      disabled={!club.netRates.netRateEnabled}
                    >
                      + {t("NetRatePage.form.addRateMonthly")}
                    </Button>


                  </div>

                  
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>


      <div className="d-flex justify-content-center">
        <Button className="m-4 button-sm button-primary button-rounded button-min fw600 fontsize-13" onClick={submitHandler}>
          {t("NetRatePage.form.submit")}
        </Button>
      </div>

      {/* Tee Time Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("NetRatePage.form.teeTimes")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t("NetRatePage.form.time")}</th>
                <th>{t("NetRatePage.form.rate")}</th>
              </tr>
            </thead>
            <tbody>
              {teeTimes.map((tt, i) => (
                <tr key={i}>
                  <td>{tt.time}</td>
                  <td>{tt.rate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/*  Calendar modal*/}
      <Modal show={showYearModal} onHide={() => setShowYearModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Select Year for Monthly Rates</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Year</Form.Label>
            <Form.Select
              value={yearForMonthlyRates}
              onChange={(e) => setYearForMonthlyRates(Number(e.target.value))}
            >
              {Array.from({ length: 6 }, (_, i) => {
                const year = DateTime.now().year + i; // current year + next 5
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowYearModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (selectedClubForYearAdd) {
                addMonthlyRatesByYear(selectedClubForYearAdd, yearForMonthlyRates);
              }
              setShowYearModal(false);
            }}
          >
            Add Rates
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default TeeTimeManager;
